import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { FsApi, RequestConfig } from '@firestitch/api';

import { Observable } from 'rxjs';

import { FundDiseaseType, FundStatus } from '@common/enums';

import { FundQueryMethod } from '@app/common/enums';
import { Fund } from '@app/common/interfaces';


@Injectable({
  providedIn: 'root',
})
export class FundData<T = any> {
  private _api = inject(FsApi);
  private _sanitizer = inject(DomSanitizer);


  public create(data: Fund & { safeWebsiteUrl?: any; showNotes?: boolean } = { id: null }): Fund {
    data.queryMethod = data.queryMethod || FundQueryMethod.Manual;
    data.diseaseType = data.diseaseType || FundDiseaseType.All;
    data.diseases = data.diseases || [];
    data.states = data.states || [];
    data.fundSupportTypes = data.fundSupportTypes || [];
    data.fundInsuranceTypes = data.fundInsuranceTypes || [];
    data.status = data.status || FundStatus.Unknown;
    data.hasIncomeRequirement = data.hasIncomeRequirement === undefined ? true : data.hasIncomeRequirement;

    if (!data.url) {
      data.url = data.websiteUrl;
    }

    data.safeWebsiteUrl = data.websiteUrl ? this._sanitizer.bypassSecurityTrustResourceUrl(data.websiteUrl) : null;
    data.showNotes = data.queryMethod === FundQueryMethod.Manual &&
      data.status &&
      data.status !== FundStatus.Unknown &&
      !!data.notes;

    return data;
  }

  public get(id: number, query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.get(
      `funds/${id}`,
      query,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public gets(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds',
      query,
      {
        key: 'funds',
        ...config,
      },
    );
  }

  public states(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds/states',
      query,
      {
        key: 'states',
        ...config,
      },
    );
  }

  /** A short-lived link to the CSV file of the funds the filters sent match. */
  public export(data: any = {}, config: RequestConfig = {}): Observable<string> {
    return this._api.post(
      'funds/export',
      data,
      {
        key: 'url',
        ...config,
      },
    );
  }


  public put(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.put(
      `funds/${data.id}`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public post(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      'funds',
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public bulk(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      'funds/bulk',
      data,
      {
        key: null,
        ...config,
      },
    );
  }

  public postViews(fundId: number, data: any = {}, config: RequestConfig = {}): Observable<T> {
    data.fundId = fundId;

    return this._api.post(
      'funds/views',
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public delete(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.delete(
      `funds/${data.id}`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public query(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      `funds/${data.id}/query`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }


  public debug(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      `funds/${data.id}/debug`,
      data,
      {
        ...config,
      },
    );
  }


  public checkScrapers(config: RequestConfig = {}): Observable<T> {
    return this._api.stream('POST', 'funds/checkscraper', {}, config);
  }

  public save(data: any): Observable<T> {
    return (data.id)
      ? this.put(data)
      : this.post(data);
  }

}
